import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '@feathr/blackbox';
import { getDisplayDimensionsForCampaign } from '@feathr/blackbox';
import { Button, Modal } from '@feathr/components';

import { AdDimensionsSelect } from '../AdDimensionsSelect';
import CreativeModal from '../CreativeModal';
import type { IUseAddAdTagCreativeParams } from './AddAdTagCreative.hooks';
import { useAddAdTagCreative } from './AddAdTagCreative.hooks';

export interface IAddAdTagCreativeProps extends IUseAddAdTagCreativeParams {
  /** Campaign for click-through tracking in ad preview. */
  campaign?: Campaign;
  /** Whether the modal is open. */
  isOpen: boolean;
}

/**
 * Two-step flow for adding a Display Ad Tag creative.
 *
 * Step 1: Dimension selection modal — user picks an ad size.
 * Step 2: CreativeModal opens with the selected dimensions pre-configured.
 */
function AddAdTagCreative({
  campaign,
  isOpen,
  campaignId,
  eventId,
  creatives,
  onAppear,
  onComplete,
  onBack,
  onCancel,
}: Readonly<IAddAdTagCreativeProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    step,
    creative,
    selectedSpec,
    handleDimensionSelect,
    handleDimensionNext,
    handleDimensionBack,
    isSaving,
    handleSave,
    handleCancel,
  } = useAddAdTagCreative({
    campaignId,
    eventId,
    creatives,
    onAppear,
    onComplete,
    onBack,
    onCancel,
  });

  if (step === 'dimensions') {
    const leftActions = (
      <Button key={'back'} name={'back'} onClick={handleDimensionBack}>
        {t('Back')}
      </Button>
    );

    const rightActions = [
      <Button key={'cancel'} name={'cancel'} onClick={handleCancel}>
        {t('Cancel')}
      </Button>,
      <Button
        disabled={!selectedSpec}
        key={'next'}
        name={'next'}
        onClick={handleDimensionNext}
        type={'primary'}
      >
        {t('Next')}
      </Button>,
    ];

    return (
      <Modal
        allowOverflow={true}
        description={t('Select the dimensions for your ad tag creative.')}
        leftActions={leftActions}
        onClose={handleCancel}
        opened={isOpen}
        rightActions={rightActions}
        title={t('Choose ad tag dimensions')}
      >
        <AdDimensionsSelect
          {...(campaign
            ? { dimensions: getDisplayDimensionsForCampaign(campaign) }
            : { channel: 'display' as const })}
          label={t('Dimensions')}
          menuPortalTarget={document.body}
          name={'ad-tag-dimensions'}
          onChange={handleDimensionSelect}
          placeholder={t('Search ad dimensions')}
          value={selectedSpec}
        />
      </Modal>
    );
  }

  return (
    <CreativeModal
      campaign={campaign}
      creative={creative}
      isSaving={isSaving}
      onClose={handleCancel}
      onSave={handleSave}
      opened={isOpen}
    />
  );
}

export default observer(AddAdTagCreative);
